import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Text, Button } from '@cuemath/web-comps';
import { COLORS } from '@cuemath/comps';

// Import components
import VideoPlayer from './components/VideoPlayer';
import AnnotationForm from './components/AnnotationForm';
import AnnotationList from './components/AnnotationList';
import TranscriptPanel from './components/TranscriptPanel';
import SalesPitchPanel from './components/SalesPitchPanel';
import AIAnalysisPanel from './components/AIAnalysisPanel';
import ErrorBoundary from '../../comps/ErrorBoundary';
import { useConfigPost } from '../../actions/configs';
import {
  checkTranscriptStatus,
  extractVideoIdFromS3Url,
  getAnnotations,
  createAnnotation as createAnnotationAPI,
  updateAnnotation as updateAnnotationAPI,
  deleteAnnotation as deleteAnnotationAPI,
  updateTrialAnnotationStatus,
  getOrCreateSalesPitch,
  checkSalesPitchStatus,
  getOrCreateAIAnalysis,
  checkAIAnalysisStatus,
} from '../../api/trial-analytics';
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  TRANSCRIPT_POLLING,
  SALES_PITCH_POLLING,
  AI_ANALYSIS_POLLING,
  ANNOTATION_EMOTIONS,
} from '../../constants/trial-annotation';
import { getCachedTranscript, setCachedTranscript } from '../../utils/transcript-cache';
import { getCookie } from '../../utils';
import { useTrialPermissions } from '../../hooks/useTrialDBHelper';
import { parseBracketTimestamp } from 'utils';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';

const Container = styled.div`
  width: 100%;
  height: inherit;
  margin: 0 auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  max-width: 1600px;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 16px 0;
  margin-bottom: 24px;
  border-bottom: 1px solid ${COLORS.GRAY_LIGHT};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TrialDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 2px 0;
`;

const TrialTitle = styled(Text)`
  margin: 0;
  padding: 0;
  font-weight: 600;
  line-height: 1.2;
`;

const TrialMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  color: ${COLORS.GRAY_DARK};
  margin: 0;
  padding: 0;
`;

const TrialTags = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tag = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: #6c757d;
  color: white;
`;

const RegionTag = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    if (props.region === 'NAM') return '#007bff';
    if (props.region === 'ISC') return '#28a745';
    if (props.region === 'EMEA') return '#6f42c1';
    if (props.region === 'APAC') return '#17a2b8';
    return '#6c757d';
  }};
`;

const SubjectTag = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    const subject = props.subject?.toUpperCase();
    switch (subject) {
      case 'MATH':
        return '#1EB5F0'; // Blue 4
      case 'CODING':
        return '#3DD771'; // Green 4
      case 'ELA':
        return '#FFBA07'; // Yellow 4
      case 'SAT':
        return '#ED6CEF'; // Purple 4
      case 'ELP':
        return '#FF7E3B'; // Orange 4
      case 'SCIENCE':
        return '#808080'; // Grey 4
      default:
        return '#6c757d'; // Default gray for unknown subjects
    }
  }};
  color: ${props => {
    const subject = props.subject?.toUpperCase();
    return subject === 'ELA' ? '#000000' : '#ffffff'; // Black text for yellow, white for others
  }};
`;

const EnrolledBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => {
    if (props.enrolled === 'Yes') return '#d4edda';
    if (props.enrolled && props.enrolled !== '') return '#f8d7da';
    return COLORS.GRAY; // grey for 'Unknown' or blank
  }};
  color: ${props => {
    if (props.enrolled === 'Yes') return '#155724';
    if (props.enrolled && props.enrolled !== '') return '#721c24';
    return 'white'; // white text on grey for 'Unknown'
  }};
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DurationDisplay = styled.div`
  text-align: right;
`;

const DurationLabel = styled(Text)`
  font-size: 10px;
  color: ${COLORS.GRAY_DARK};
  margin: 0;
`;

const DurationValue = styled(Text)`
  font-family: monospace;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.COLA};
  margin: 0;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 24px;
  flex: 1;
  min-height: 0;
  max-width: 100%;
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  overflow: hidden;
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 600px;
  min-width: 0;
  overflow: hidden;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${COLORS.GRAY};
  margin-bottom: 16px;
`;

const Tab = styled.button`
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-bottom: 2px solid ${props => (props.active ? COLORS.COLA : 'transparent')};
  color: ${props => (props.active ? COLORS.COLA : COLORS.GRAY_DARK)};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${COLORS.COLA};
  }
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
`;

const ErrorText = styled(Text)`
  color: ${COLORS.RED_DARK};
  margin-bottom: 16px;
`;

// Helper function to transform API annotation data to frontend format
const transformAnnotationData = apiAnnotation => ({
  annotationId: apiAnnotation.annotation_id,
  trialId: apiAnnotation.video_id, // API uses video_id, frontend expects trialId
  reviewerId: apiAnnotation.reviewer_id,
  reviewerName: apiAnnotation.reviewer_name,
  trialPart: apiAnnotation.trial_part,
  emotion: apiAnnotation.emotion,
  timestamp: {
    start: parseFloat(apiAnnotation.timestamp.start), // Convert string decimal to float
    end: apiAnnotation.timestamp.end
      ? parseFloat(apiAnnotation.timestamp.end)
      : undefined, // Convert string decimal to float
  },
  content: apiAnnotation.content,
  createdAt: apiAnnotation.created_at,
  updatedAt: apiAnnotation.updated_at,
});

// Helper function to transform frontend annotation data to API format
const transformToApiFormat = (frontendAnnotation, videoId) => ({
  content: frontendAnnotation.content,
  timestamp: {
    start: frontendAnnotation.timestamp.start.toString(), // Convert to string for backend
    end: frontendAnnotation.timestamp.end?.toString(), // Convert to string for backend
  },
  emotion: frontendAnnotation.emotion,
  trial_part: frontendAnnotation.trialPart,
});

const extractAIAnalysisData = analysisResponse => {
  if (!analysisResponse) {
    return null;
  }

  // Gateway responses come in a few shapes depending on which service handled the request:
  // 1. BaseResourcesView wraps the service payload under `data`
  // 2. Service may return the analysis directly as `analysis`
  // 3. Some gateways double-wrap under `value.data.analysis`
  // This helper tries each known shape before falling back to the raw object.
  if (analysisResponse.data?.analysis) {
    return analysisResponse.data.analysis;
  }

  if (analysisResponse.analysis) {
    return analysisResponse.analysis;
  }

  if (analysisResponse.value?.data?.analysis) {
    return analysisResponse.value.data.analysis;
  }

  return analysisResponse;
};

function AnnotatePage() {
  const { teacherClassroomId } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Get trial data from router state
  const trialFromRouter = location.state?.trial;

  const [trialData] = useState(trialFromRouter);
  const [trialLoading, setTrialLoading] = useState(!trialFromRouter);

  // Data loading state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [rawVideoUrl, setRawVideoUrl] = useState(null);

  // Transcript state
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptProcessing, setTranscriptProcessing] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [transcriptMessage, setTranscriptMessage] = useState(null);
  const pollingIntervalRef = useRef(null);

  // Sales Pitch state
  const [salesPitch, setSalesPitch] = useState(null);
  const [salesPitchLoading, setSalesPitchLoading] = useState(false);
  const [salesPitchProcessing, setSalesPitchProcessing] = useState(false);
  const [salesPitchError, setSalesPitchError] = useState(null);
  const [salesPitchMessage, setSalesPitchMessage] = useState(null);
  const salesPitchPollingIntervalRef = useRef(null);

  // AI Analysis state
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [aiAnalysisProcessing, setAiAnalysisProcessing] = useState(false);
  const [aiAnalysisError, setAiAnalysisError] = useState(null);
  const [aiAnalysisMessage, setAiAnalysisMessage] = useState(null);
  const aiAnalysisPollingIntervalRef = useRef(null);

  // Video state
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playerReady, setPlayerReady] = useState(false);

  // Update duration when video loads
  const handleDurationChange = useCallback(newDuration => {
    setDuration(newDuration);
  }, []);

  // Update player ready state
  const handlePlayerReady = useCallback(() => {
    setPlayerReady(true);
  }, []);

  // Annotation state
  const [annotations, setAnnotations] = useState([]);
  const [annotationsLoading, setAnnotationsLoading] = useState(false);
  const [isCreatingAnnotation, setIsCreatingAnnotation] = useState(false);
  const [annotationStart, setAnnotationStart] = useState(null);
  const [annotationEnd, setAnnotationEnd] = useState(null);
  const [annotationContent, setAnnotationContent] = useState('');
  const [annotationPart, setAnnotationPart] = useState('Trial Part 1');
  const [annotationEmotion, setAnnotationEmotion] = useState(
    ANNOTATION_EMOTIONS.NEGATIVE,
  );

  // Editing state
  const [editingAnnotation, setEditingAnnotation] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editPart, setEditPart] = useState('Trial Part 1');
  const [editEmotion, setEditEmotion] = useState(ANNOTATION_EMOTIONS.NEGATIVE);

  // UI state
  const [activeTab, setActiveTab] = useState('transcript');
  const [newlyCreatedAnnotation, setNewlyCreatedAnnotation] = useState(null);
  const [currentUserId] = useState(() => {
    const cookieUserId = getCookie('user_id') || getCookie('emp_id');
    // For dev: fallback to hardcoded user ID when cookie is not available
    return cookieUserId || '1bfd0c1e-ec95-11e8-9b4a-02420a0000e2';
  });

  // Get user permissions for trial dashboard features
  const {
    canGenerateTranscript,
    canCreateAnnotation,
    canGenerateSalesPitch,
  } = useTrialPermissions();

  // AI Analysis uses same permission as transcript generation
  const canGenerateAIAnalysis = canGenerateTranscript;

  // Data - use trial data from either router state or API fetch
  const trial = trialData;

  // Hook for generating AWS signed URLs
  const { post: createAWSSignedUrl } = useConfigPost({
    onComplete: (errorMessage, signedUrl) => {
      if (errorMessage) {
        console.error('Error creating signed URL:', errorMessage);
        setError(ERROR_MESSAGES.VIDEO.SIGNED_URL_FAILED);
        setLoading(false);
      } else {
        setVideoUrl(signedUrl);
        setLoading(false);
      }
    },
  });

  // Use trial data from router state; direct URL access is not supported
  useEffect(() => {
    if (trialFromRouter) {
      setTrialLoading(false);
      return;
    }

    setError(ERROR_MESSAGES.VIDEO.NO_TRIAL_DATA);
    setTrialLoading(false);
    setLoading(false);
  }, [trialFromRouter]);

  // Track page view when trial data is loaded
  useEffect(() => {
    if (trialData && !trialLoading) {
      trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_PAGE_VIEW, {
        teacher_classroom_id: teacherClassroomId,
        region: trialData.region,
        subject: trialData.subject,
        grade: trialData.grade,
        trial_version: trialData.trialVersion,
        user_id: currentUserId,
      });
    }
  }, [trialData, trialLoading, teacherClassroomId, currentUserId]);

  // Extract video URL from trial data
  useEffect(() => {
    // Wait for trial data to be loaded
    if (trialLoading) {
      return;
    }

    if (!trialData) {
      setError(ERROR_MESSAGES.VIDEO.NO_TRIAL_DATA);
      setLoading(false);
      return;
    }

    // Extract video links directly from transformed trial data
    const videoLinks = trialData.videoLinks;

    if (!videoLinks || videoLinks.length === 0) {
      setError(ERROR_MESSAGES.VIDEO.NO_VIDEO_AVAILABLE);
      setLoading(false);
      return;
    }

    // Use the first video link (videoLinks is always an array from transformation)
    const videoUrl = videoLinks[0];

    if (!videoUrl || typeof videoUrl !== 'string') {
      setError(ERROR_MESSAGES.VIDEO.INVALID_URL);
      setLoading(false);
      return;
    }

    setRawVideoUrl(videoUrl);
    // Don't set loading to false yet - wait for signed URL generation
  }, [trialData, trialLoading]);

  // Generate signed URL when raw video URL is available
  useEffect(() => {
    if (!rawVideoUrl) {
      return;
    }

    try {
      // Parse the S3 URL directly
      // Format: https://s3-ap-southeast-1.amazonaws.com/cuemath-tokbox/46187592/.../archive.mp4
      const url = new URL(rawVideoUrl);
      const pathname = url.pathname; // /cuemath-tokbox/46187592/.../archive.mp4

      // Extract bucket and key from pathname
      const pathParts = pathname.split('/').filter(part => part !== '');
      const bucket = pathParts[0]; // cuemath-tokbox
      const key = pathParts.slice(1).join('/'); // 46187592/.../archive.mp4

      if (!bucket || !key) {
        throw new Error('Failed to extract bucket or key from video URL');
      }

      createAWSSignedUrl({ bucket, key });
    } catch (error) {
      console.error('Error parsing video URL:', error);
      setError(ERROR_MESSAGES.VIDEO.PARSE_FAILED);
      setLoading(false);
    }
  }, [rawVideoUrl, createAWSSignedUrl]);

  // Helper function to transform transcript data
  const transformTranscriptData = useCallback(transcriptData => {
    if (!transcriptData || !transcriptData.value) {
      return null;
    }

    const data = transcriptData.value;

    // Check if transcript exists and is completed
    if (data.status === 'success' && data.data && data.data.utterances) {
      const transformedTranscript = {
        segments: data.data.utterances.map((item, index) => ({
          id: index + 1,
          speaker: item.speaker_tag || `Speaker ${item.speaker_id || index + 1}`,
          text: item.transcript || '',
          startTime: item.start_time || 0,
          endTime: item.end_time || 0,
          confidence: item.confidence || 0,
        })),
        // Preserve the raw Sonus utterances so downstream triggers
        // (sales pitch / AI analysis) can forward them to their Lambdas
        // without a refetch — see cuemath/inteladmingateway#357.
        utterances: data.data.utterances,
      };

      return transformedTranscript;
    } else if (data.status === 'processing') {
      return null;
    } else {
      return null;
    }
  }, []);

  // Polling function for transcript completion
  const startPolling = useCallback(() => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Extract video ID for polling
    let videoId;
    try {
      videoId = extractVideoIdFromS3Url(rawVideoUrl);
    } catch (error) {
      setTranscriptError(`Failed to extract video ID: ${error.message}`);
      setTranscriptProcessing(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = TRANSCRIPT_POLLING.MAX_ATTEMPTS;

    const pollTranscript = async () => {
      attempts++;

      try {
        const result = await checkTranscriptStatus({
          objectId: teacherClassroomId,
          videoId,
        });

        if (result.exists && !result.processing) {
          // Transcript is ready!
          const transformedTranscript = transformTranscriptData(result.data);
          setTranscript(transformedTranscript);
          setTranscriptProcessing(false);
          setTranscriptMessage(SUCCESS_MESSAGES.TRANSCRIPT.LOADED);

          // Track success
          trackEvent(EVENT_TYPE.TRIAL_TRANSCRIPT_GENERATED, {
            teacher_classroom_id: teacherClassroomId,
            user_id: currentUserId,
          });

          // Cache the completed transcript
          setCachedTranscript(videoId, result.data);

          // Clear polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Clear success message after configured duration
          setTimeout(
            () => setTranscriptMessage(null),
            TRANSCRIPT_POLLING.MESSAGE_DURATION_MS,
          );
        } else if (attempts >= maxAttempts) {
          // Timeout
          setTranscriptProcessing(false);
          setTranscriptError(ERROR_MESSAGES.TRANSCRIPT.TIMEOUT);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_TRANSCRIPT_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: ERROR_MESSAGES.TRANSCRIPT.TIMEOUT,
            user_id: currentUserId,
          });

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
        // If still processing and under max attempts, continue polling silently
      } catch (error) {
        // Continue polling on error unless max attempts reached
        if (attempts >= maxAttempts) {
          setTranscriptProcessing(false);
          setTranscriptError(ERROR_MESSAGES.TRANSCRIPT.STATUS_CHECK_FAILED);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_TRANSCRIPT_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: error.message || ERROR_MESSAGES.TRANSCRIPT.STATUS_CHECK_FAILED,
            user_id: currentUserId,
          });

          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
        }
      }
    };

    // Start polling immediately, then every 30 seconds
    pollTranscript();
    pollingIntervalRef.current = setInterval(
      pollTranscript,
      TRANSCRIPT_POLLING.INTERVAL_MS,
    );
  }, [rawVideoUrl, transformTranscriptData, teacherClassroomId, currentUserId]);

  // Polling function for sales pitch completion
  const startSalesPitchPolling = useCallback(() => {
    // Clear any existing polling
    if (salesPitchPollingIntervalRef.current) {
      clearInterval(salesPitchPollingIntervalRef.current);
      salesPitchPollingIntervalRef.current = null;
    }

    if (!teacherClassroomId) {
      setSalesPitchError('Classroom ID is required for sales pitch generation');
      setSalesPitchProcessing(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = SALES_PITCH_POLLING.MAX_ATTEMPTS;

    const pollSalesPitch = async () => {
      attempts++;

      try {
        const result = await checkSalesPitchStatus(teacherClassroomId);

        if (result.exists && !result.processing) {
          // Sales pitch is ready!
          setSalesPitch(result.data);
          setSalesPitchProcessing(false);
          setSalesPitchMessage(SUCCESS_MESSAGES.SALES_PITCH.LOADED);

          // Track success
          trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATED, {
            teacher_classroom_id: teacherClassroomId,
            user_id: currentUserId,
          });

          // Clear polling
          if (salesPitchPollingIntervalRef.current) {
            clearInterval(salesPitchPollingIntervalRef.current);
            salesPitchPollingIntervalRef.current = null;
          }

          // Clear success message after configured duration
          setTimeout(
            () => setSalesPitchMessage(null),
            SALES_PITCH_POLLING.MESSAGE_DURATION_MS,
          );

          // Fetch transcript since it's automatically created during sales pitch generation
          if (rawVideoUrl && !transcript) {
            try {
              const videoId = extractVideoIdFromS3Url(rawVideoUrl);
              const transcriptResult = await checkTranscriptStatus({
                objectId: teacherClassroomId,
                videoId,
              });

              if (transcriptResult.exists && !transcriptResult.processing) {
                // Transcript is ready
                const transformedTranscript = transformTranscriptData(
                  transcriptResult.data,
                );
                setTranscript(transformedTranscript);
                setTranscriptProcessing(false);

                // Cache the transcript for future use
                setCachedTranscript(videoId, transcriptResult.data);
              }
            } catch (error) {
              // Silently fail - user can manually generate if needed
            }
          }
        } else if (attempts >= maxAttempts) {
          // Timeout
          setSalesPitchProcessing(false);
          setSalesPitchError(ERROR_MESSAGES.SALES_PITCH.TIMEOUT);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: ERROR_MESSAGES.SALES_PITCH.TIMEOUT,
            user_id: currentUserId,
          });

          if (salesPitchPollingIntervalRef.current) {
            clearInterval(salesPitchPollingIntervalRef.current);
            salesPitchPollingIntervalRef.current = null;
          }
        }
        // If still processing and under max attempts, continue polling silently
      } catch (error) {
        // Continue polling on error unless max attempts reached
        if (attempts >= maxAttempts) {
          setSalesPitchProcessing(false);
          setSalesPitchError(ERROR_MESSAGES.SALES_PITCH.STATUS_CHECK_FAILED);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: error.message || ERROR_MESSAGES.SALES_PITCH.STATUS_CHECK_FAILED,
            user_id: currentUserId,
          });

          if (salesPitchPollingIntervalRef.current) {
            clearInterval(salesPitchPollingIntervalRef.current);
            salesPitchPollingIntervalRef.current = null;
          }
        }
      }
    };

    // Start polling immediately, then every 30 seconds
    pollSalesPitch();
    salesPitchPollingIntervalRef.current = setInterval(
      pollSalesPitch,
      SALES_PITCH_POLLING.INTERVAL_MS,
    );
  }, [
    teacherClassroomId,
    rawVideoUrl,
    transcript,
    transformTranscriptData,
    currentUserId,
  ]);

  const startAIAnalysisPolling = useCallback(() => {
    if (aiAnalysisPollingIntervalRef.current) {
      clearInterval(aiAnalysisPollingIntervalRef.current);
      aiAnalysisPollingIntervalRef.current = null;
    }

    if (!teacherClassroomId) {
      setAiAnalysisError(ERROR_MESSAGES.AI_ANALYSIS.INVALID_CLASSROOM_ID);
      setAiAnalysisProcessing(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = AI_ANALYSIS_POLLING.MAX_ATTEMPTS;

    const pollAIAnalysis = async () => {
      attempts += 1;

      try {
        const result = await checkAIAnalysisStatus(teacherClassroomId);

        if (result.exists && !result.processing) {
          const analysisData = extractAIAnalysisData(result.data);
          setAiAnalysis(analysisData || null);
          setAiAnalysisProcessing(false);
          setAiAnalysisMessage(SUCCESS_MESSAGES.AI_ANALYSIS.LOADED);

          // Track success
          trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATED, {
            teacher_classroom_id: teacherClassroomId,
            issue_count: analysisData?.issues?.length || 0,
            user_id: currentUserId,
          });

          if (aiAnalysisPollingIntervalRef.current) {
            clearInterval(aiAnalysisPollingIntervalRef.current);
            aiAnalysisPollingIntervalRef.current = null;
          }

          setTimeout(
            () => setAiAnalysisMessage(null),
            AI_ANALYSIS_POLLING.MESSAGE_DURATION_MS,
          );
        } else if (attempts >= maxAttempts) {
          setAiAnalysisProcessing(false);
          setAiAnalysisError(ERROR_MESSAGES.AI_ANALYSIS.TIMEOUT);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: ERROR_MESSAGES.AI_ANALYSIS.TIMEOUT,
            user_id: currentUserId,
          });

          if (aiAnalysisPollingIntervalRef.current) {
            clearInterval(aiAnalysisPollingIntervalRef.current);
            aiAnalysisPollingIntervalRef.current = null;
          }
        }
      } catch (error) {
        if (attempts >= maxAttempts) {
          setAiAnalysisProcessing(false);
          setAiAnalysisError(ERROR_MESSAGES.AI_ANALYSIS.STATUS_CHECK_FAILED);

          // Track failure
          trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATION_FAILED, {
            teacher_classroom_id: teacherClassroomId,
            error: error.message || ERROR_MESSAGES.AI_ANALYSIS.STATUS_CHECK_FAILED,
            user_id: currentUserId,
          });

          if (aiAnalysisPollingIntervalRef.current) {
            clearInterval(aiAnalysisPollingIntervalRef.current);
            aiAnalysisPollingIntervalRef.current = null;
          }
        }
      }
    };

    pollAIAnalysis();
    aiAnalysisPollingIntervalRef.current = setInterval(
      pollAIAnalysis,
      AI_ANALYSIS_POLLING.INTERVAL_MS,
    );
  }, [teacherClassroomId, currentUserId]);

  // Check for existing transcript when component mounts (without auto-generating)
  useEffect(() => {
    const checkExistingTranscript = async () => {
      if (!teacherClassroomId || !rawVideoUrl) {
        return;
      }

      // Extract video ID for caching
      let videoId;
      try {
        videoId = extractVideoIdFromS3Url(rawVideoUrl);
      } catch (error) {
        setTranscriptError(ERROR_MESSAGES.TRANSCRIPT.INVALID_VIDEO_ID);
        return;
      }

      // Check cache first
      const cachedTranscript = getCachedTranscript(videoId);
      if (cachedTranscript) {
        // Use cached transcript
        const transformedTranscript = transformTranscriptData(cachedTranscript);
        if (transformedTranscript) {
          setTranscript(transformedTranscript);
          setTranscriptProcessing(false);
          setTranscriptLoading(false);
          return;
        }
      }

      // Check if transcript exists (without creating)
      setTranscriptLoading(true);
      try {
        const result = await checkTranscriptStatus({
          objectId: teacherClassroomId,
          videoId,
        });

        if (result.exists && !result.processing) {
          // Transcript is ready
          const transformedTranscript = transformTranscriptData(result.data);
          setTranscript(transformedTranscript);
          setTranscriptProcessing(false);

          // Cache the transcript for future use
          setCachedTranscript(videoId, result.data);
        } else if (result.processing) {
          // Transcript is being generated - start polling
          setTranscriptProcessing(true);
          setTranscriptMessage(result.message || SUCCESS_MESSAGES.TRANSCRIPT.POLLING);

          // If transcript exists but is processing, transform and display what we have
          if (result.data && result.data.transcript) {
            const transformedTranscript = transformTranscriptData(result.data);
            setTranscript(transformedTranscript);
          }

          // Start polling for completion
          startPolling();
        }
      } catch (error) {
        // No transcript exists yet - this is fine, user will manually generate
        setTranscript(null);
        setTranscriptProcessing(false);
      } finally {
        setTranscriptLoading(false);
      }
    };

    checkExistingTranscript();

    // Cleanup polling on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [teacherClassroomId, rawVideoUrl, transformTranscriptData, startPolling]);

  // Check for existing sales pitch when component mounts (without auto-generating)
  useEffect(() => {
    const checkExistingSalesPitch = async () => {
      if (!teacherClassroomId) {
        return;
      }

      // Check if sales pitch exists (without creating)
      setSalesPitchLoading(true);
      try {
        const result = await checkSalesPitchStatus(teacherClassroomId);

        if (result.exists && !result.processing) {
          // Sales pitch is ready
          setSalesPitch(result.data);
          setSalesPitchProcessing(false);
        } else {
          // No sales pitch exists yet - this is fine, user will manually generate
          setSalesPitch(null);
          setSalesPitchProcessing(false);
        }
      } catch (error) {
        // No sales pitch exists yet - this is fine, user will manually generate
        setSalesPitch(null);
        setSalesPitchProcessing(false);
      } finally {
        setSalesPitchLoading(false);
      }
    };

    checkExistingSalesPitch();

    // Cleanup polling on unmount
    return () => {
      if (salesPitchPollingIntervalRef.current) {
        clearInterval(salesPitchPollingIntervalRef.current);
      }
    };
  }, [teacherClassroomId, startSalesPitchPolling]);

  useEffect(() => {
    const checkExistingAIAnalysis = async () => {
      if (!teacherClassroomId) {
        return;
      }

      setAiAnalysisLoading(true);
      setAiAnalysisError(null);

      try {
        const result = await checkAIAnalysisStatus(teacherClassroomId);

        if (result.exists && !result.processing) {
          const analysisData = extractAIAnalysisData(result.data);
          setAiAnalysis(analysisData || null);
          setAiAnalysisProcessing(false);
        } else if (result.processing) {
          setAiAnalysisProcessing(true);
          setAiAnalysisMessage(result.message || SUCCESS_MESSAGES.AI_ANALYSIS.POLLING);
          startAIAnalysisPolling();
        } else {
          setAiAnalysis(null);
          setAiAnalysisProcessing(false);
          setAiAnalysisMessage(null);
        }
      } catch (error) {
        console.error('[AIAnalysis] Failed to check existing analysis', error);
        setAiAnalysis(null);
        setAiAnalysisProcessing(false);
        setAiAnalysisMessage(null);
      } finally {
        setAiAnalysisLoading(false);
      }
    };

    checkExistingAIAnalysis();
    // We intentionally omit startAIAnalysisPolling because the check should only re-run
    // when the classroom changes; the polling callback is stable for a given classroom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teacherClassroomId]);

  // Manual sales pitch generation function
  const handleGenerateSalesPitch = async () => {
    // Check if user has permission to generate sales pitches
    if (!canGenerateSalesPitch) {
      alert('You do not have permission to generate sales pitches');
      return;
    }

    if (!teacherClassroomId || !rawVideoUrl) {
      return;
    }

    // Sales pitch trigger now requires the Sonus utterances the transcript
    // panel already loaded — gateway forwards them to the Lambda
    // (cuemath/inteladmingateway#357). Bail with a clear error if they
    // are not yet available rather than firing a payload-less request.
    const utterances = transcript?.utterances;
    if (!utterances || utterances.length === 0) {
      setSalesPitchError(ERROR_MESSAGES.SALES_PITCH.FETCH_FAILED);
      return;
    }

    trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATE_CLICKED, {
      teacher_classroom_id: teacherClassroomId,
      user_id: currentUserId,
    });

    setSalesPitchLoading(true);
    setSalesPitchError(null);
    setSalesPitchMessage(null);

    try {
      const result = await getOrCreateSalesPitch(teacherClassroomId, utterances);

      if (result.exists && !result.processing) {
        // Sales pitch is ready
        trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATED, {
          teacher_classroom_id: teacherClassroomId,
          user_id: currentUserId,
        });
        setSalesPitch(result.data);
        setSalesPitchProcessing(false);
        setSalesPitchMessage(SUCCESS_MESSAGES.SALES_PITCH.LOADED);
        setTimeout(
          () => setSalesPitchMessage(null),
          SALES_PITCH_POLLING.MESSAGE_DURATION_MS,
        );
      } else if (result.processing) {
        // Sales pitch is being generated - start polling
        setSalesPitchProcessing(true);
        setSalesPitchMessage(SUCCESS_MESSAGES.SALES_PITCH.DURATION_MESSAGE);

        // Start polling for completion
        startSalesPitchPolling();
      } else if (result.error) {
        // Error occurred
        setSalesPitchError(result.error);
        setSalesPitchProcessing(false);
      }
    } catch (error) {
      trackEvent(EVENT_TYPE.TRIAL_SALES_PITCH_GENERATION_FAILED, {
        teacher_classroom_id: teacherClassroomId,
        error: error.message,
        user_id: currentUserId,
      });
      setSalesPitchError(error.message || ERROR_MESSAGES.SALES_PITCH.FETCH_FAILED);
      setSalesPitchProcessing(false);
    } finally {
      setSalesPitchLoading(false);
    }
  };

  // Manual AI Analysis generation function
  const handleGenerateAIAnalysis = async () => {
    // Check if user has permission to generate AI analysis
    if (!canGenerateAIAnalysis) {
      alert('You do not have permission to generate AI analysis');
      return;
    }

    if (!teacherClassroomId || !rawVideoUrl) {
      return;
    }

    // AI analysis trigger now requires the Sonus utterances the transcript
    // panel already loaded — gateway forwards them to the Lambda
    // (cuemath/inteladmingateway#357). Bail with a clear error if they
    // are not yet available rather than firing a payload-less request.
    const utterances = transcript?.utterances;
    if (!utterances || utterances.length === 0) {
      setAiAnalysisError(ERROR_MESSAGES.AI_ANALYSIS.FETCH_FAILED);
      return;
    }

    trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATE_CLICKED, {
      teacher_classroom_id: teacherClassroomId,
      user_id: currentUserId,
    });

    setAiAnalysisLoading(true);
    setAiAnalysisError(null);
    setAiAnalysisMessage(null);

    try {
      const result = await getOrCreateAIAnalysis(teacherClassroomId, utterances);

      if (result.exists && !result.processing) {
        const analysisData = extractAIAnalysisData(result.data);
        trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATED, {
          teacher_classroom_id: teacherClassroomId,
          issue_count: analysisData?.issues?.length || 0,
          user_id: currentUserId,
        });
        setAiAnalysis(analysisData || null);
        setAiAnalysisProcessing(false);
        setAiAnalysisMessage(SUCCESS_MESSAGES.AI_ANALYSIS.LOADED);
        setTimeout(
          () => setAiAnalysisMessage(null),
          AI_ANALYSIS_POLLING.MESSAGE_DURATION_MS,
        );
      } else if (result.processing) {
        setAiAnalysisProcessing(true);
        setAiAnalysisMessage(result.message || SUCCESS_MESSAGES.AI_ANALYSIS.POLLING);
        startAIAnalysisPolling();
      } else if (result.error) {
        setAiAnalysisError(result.error);
        setAiAnalysisProcessing(false);
      }
    } catch (error) {
      trackEvent(EVENT_TYPE.TRIAL_AI_ANALYSIS_GENERATION_FAILED, {
        teacher_classroom_id: teacherClassroomId,
        error: error.message,
        user_id: currentUserId,
      });
      setAiAnalysisError(error.message || ERROR_MESSAGES.AI_ANALYSIS.FETCH_FAILED);
      setAiAnalysisProcessing(false);
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  // Fetch annotations when video is available
  useEffect(() => {
    const fetchAnnotations = async () => {
      if (!rawVideoUrl) {
        return;
      }

      setAnnotationsLoading(true);

      try {
        // Extract video ID from S3 URL
        const videoId = extractVideoIdFromS3Url(rawVideoUrl);

        // Fetch annotations from API
        const response = await getAnnotations(videoId);

        // Extract annotations from the known response structure
        const annotationsData = response.value?.data || [];

        if (annotationsData.length > 0) {
          // Transform API data to frontend format
          const transformedAnnotations = annotationsData.map(transformAnnotationData);
          setAnnotations(transformedAnnotations);
        } else {
          setAnnotations([]);
        }
      } catch (error) {
        console.error('Error fetching annotations:', error);
        setAnnotations([]);
      } finally {
        setAnnotationsLoading(false);
      }
    };

    fetchAnnotations();
  }, [rawVideoUrl]);

  useEffect(() => {
    return () => {
      if (aiAnalysisPollingIntervalRef.current) {
        clearInterval(aiAnalysisPollingIntervalRef.current);
        aiAnalysisPollingIntervalRef.current = null;
      }
    };
  }, [teacherClassroomId]);

  const aiAnalysisMarkers = useMemo(() => {
    if (!aiAnalysis?.issues) {
      return [];
    }

    return aiAnalysis.issues
      .map((issue, index) => {
        const seconds = parseBracketTimestamp(issue.timestamp);
        if (seconds === null) {
          return null;
        }

        return {
          id: `ai-marker-${index}-${issue.timestamp}`,
          seconds,
          severity: issue.severity,
          theme: issue.theme,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.seconds - b.seconds);
  }, [aiAnalysis]);

  // Video handlers
  const handleTimeUpdate = useCallback(time => {
    setCurrentTime(time);
  }, []);

  const handlePlay = useCallback(() => {
    setPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setPlaying(false);
  }, []);

  const handleSeek = useCallback(time => {
    setCurrentTime(time);
  }, []);

  const handleTogglePlay = useCallback(() => {
    setPlaying(prev => !prev);
  }, []);

  const handleSeekBackward = useCallback(() => {
    handleSeek(Math.max(0, currentTime - 10));
  }, [currentTime, handleSeek]);

  const handleSeekForward = useCallback(() => {
    handleSeek(Math.min(duration, currentTime + 10));
  }, [currentTime, duration, handleSeek]);

  const handlePlaybackRateChange = useCallback(rate => {
    setPlaybackRate(rate);
  }, []);

  // Refs for video control
  const videoElementRef = useRef(null);

  // Sync playing state with video element
  useEffect(() => {
    if (videoElementRef.current) {
      if (playing) {
        videoElementRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
      } else {
        videoElementRef.current.pause();
      }
    }
  }, [playing]);

  // Sync currentTime with video element when seeking
  useEffect(() => {
    if (
      videoElementRef.current &&
      Math.abs(videoElementRef.current.currentTime - currentTime) > 0.5
    ) {
      videoElementRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  // Sync playback rate with video element
  useEffect(() => {
    if (videoElementRef.current) {
      videoElementRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Annotation handlers
  const handleStartAnnotation = useCallback(() => {
    // Check if user has permission to create annotations
    if (!canCreateAnnotation) {
      alert('You do not have permission to create annotations');
      return;
    }

    // Get the trial part from the last annotation, if any
    const lastAnnotationPart =
      annotations.length > 0
        ? annotations[annotations.length - 1].trialPart
        : 'Trial Part 1';

    setAnnotationStart(currentTime);
    setAnnotationPart(lastAnnotationPart); // Set to last annotation's trial part
    setIsCreatingAnnotation(true);
    setActiveTab('annotations');
    setPlaying(false);
  }, [currentTime, canCreateAnnotation, annotations]);

  const handleResetAnnotationForm = useCallback(() => {
    setIsCreatingAnnotation(false);
    setAnnotationStart(null);
    setAnnotationEnd(null);
    setAnnotationContent('');
    // Keep annotationPart at its current value (don't reset)
    setAnnotationEmotion(ANNOTATION_EMOTIONS.NEGATIVE);
  }, []);

  const handleCreateAnnotation = useCallback(async () => {
    // Check if user has permission to create annotations
    if (!canCreateAnnotation) {
      alert('You do not have permission to create annotations');
      return;
    }

    if (annotationStart === null || !annotationContent.trim() || !rawVideoUrl) return;

    try {
      // Extract video ID from S3 URL
      const videoId = extractVideoIdFromS3Url(rawVideoUrl);

      // Prepare annotation data for API
      const annotationData = transformToApiFormat(
        {
          content: annotationContent,
          timestamp: {
            start: annotationStart,
            end: annotationEnd || undefined,
          },
          emotion: annotationEmotion,
          trialPart: annotationPart,
        },
        videoId,
      );

      // Create annotation via API
      const response = await createAnnotationAPI(videoId, annotationData);

      if (response.success && response.data) {
        // Transform API response to frontend format
        const newAnnotation = transformAnnotationData(response.data);

        trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_CREATED, {
          teacher_classroom_id: teacherClassroomId,
          annotation_id: newAnnotation.annotationId,
          emotion: annotationEmotion,
          trial_part: annotationPart,
          content_length: annotationContent.length,
          user_id: currentUserId,
        });

        // Update local state
        setAnnotations(prev => {
          const updated = [...prev, newAnnotation];
          updated.sort((a, b) => a.timestamp.start - b.timestamp.start);
          return updated;
        });

        setNewlyCreatedAnnotation(newAnnotation.annotationId);
        setTimeout(() => setNewlyCreatedAnnotation(null), 2000);

        handleResetAnnotationForm();

        // Update trial annotation status in the database using reviewer_name from API response
        try {
          const annotatorName = newAnnotation.reviewerName;
          if (annotatorName && trial?.teacherClassroomId) {
            await updateTrialAnnotationStatus(trial.teacherClassroomId, annotatorName);
          }
        } catch (statusError) {
          // Don't throw error - annotation was created successfully
          // Silently fail the status update
        }
      } else {
        throw new Error('Failed to create annotation');
      }
    } catch (error) {
      console.error('Error creating annotation:', error);
      trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_CREATE_FAILED, {
        teacher_classroom_id: teacherClassroomId,
        error: error.message,
        user_id: currentUserId,
      });
      alert('Failed to create annotation. Please try again.');
    }
  }, [
    canCreateAnnotation,
    annotationStart,
    annotationEnd,
    teacherClassroomId,
    annotationContent,
    annotationPart,
    annotationEmotion,
    rawVideoUrl,
    handleResetAnnotationForm,
    trial,
    currentUserId,
  ]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Prevent shortcuts when typing in input fields
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      // Check if seek slider is focused
      if (e.target instanceof HTMLInputElement && e.target.type === 'range') {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          handleTogglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (e.shiftKey) {
            handleSeek(Math.max(0, currentTime - 1));
          } else {
            handleSeek(Math.max(0, currentTime - 5));
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (e.shiftKey) {
            handleSeek(Math.min(duration, currentTime + 1));
          } else {
            handleSeek(Math.min(duration, currentTime + 5));
          }
          break;
        case 'a':
        case 'A':
          e.preventDefault();
          if (!isCreatingAnnotation) {
            handleStartAnnotation();
          }
          break;
        case 'Enter':
          if (isCreatingAnnotation && annotationContent.trim()) {
            e.preventDefault();
            handleCreateAnnotation();
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (isCreatingAnnotation) {
            handleResetAnnotationForm();
          }
          break;
        default:
          // No action for other keys
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    currentTime,
    duration,
    isCreatingAnnotation,
    annotationContent,
    handleCreateAnnotation,
    handleResetAnnotationForm,
    handleSeek,
    handleStartAnnotation,
    handleTogglePlay,
  ]);

  const handleEditAnnotation = useCallback(
    annotation => {
      // Check if user has permission to create/edit annotations
      if (!canCreateAnnotation) {
        alert('You do not have permission to edit annotations');
        return;
      }

      setEditingAnnotation(annotation);
      setEditContent(annotation.content);
      setEditPart(annotation.trialPart);
      setEditEmotion(annotation.emotion);
      setActiveTab('annotations');
    },
    [canCreateAnnotation],
  );

  const handleSaveEditedAnnotation = useCallback(async () => {
    // Check if user has permission to create/edit annotations
    if (!canCreateAnnotation) {
      alert('You do not have permission to edit annotations');
      return;
    }

    if (!editingAnnotation || !editContent.trim() || !rawVideoUrl) return;

    try {
      // Extract video ID from S3 URL
      const videoId = extractVideoIdFromS3Url(rawVideoUrl);

      // Prepare update data for API
      const updateData = {
        content: editContent,
        trial_part: editPart,
        emotion: editEmotion,
      };

      // Update annotation via API
      const response = await updateAnnotationAPI(
        videoId,
        editingAnnotation.annotationId,
        updateData,
      );

      if (response.success) {
        trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_EDITED, {
          teacher_classroom_id: teacherClassroomId,
          annotation_id: editingAnnotation.annotationId,
          user_id: currentUserId,
        });

        // Update local state with the edited data (since API doesn't return updated annotation)
        setAnnotations(prev => {
          const updated = prev.map(ann =>
            ann.annotationId === editingAnnotation.annotationId
              ? {
                  ...ann,
                  content: editContent,
                  trialPart: editPart,
                  emotion: editEmotion,
                  updatedAt: new Date().toISOString(),
                }
              : ann,
          );
          updated.sort((a, b) => a.timestamp.start - b.timestamp.start);
          return updated;
        });

        setEditingAnnotation(null);
        setEditContent('');
        setEditPart('Trial Part 1');
        setEditEmotion(ANNOTATION_EMOTIONS.NEGATIVE);
      } else {
        throw new Error('Failed to update annotation');
      }
    } catch (error) {
      console.error('Error updating annotation:', error);
      trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_EDIT_FAILED, {
        teacher_classroom_id: teacherClassroomId,
        annotation_id: editingAnnotation?.annotationId,
        error: error.message,
        user_id: currentUserId,
      });
      alert('Failed to update annotation. Please try again.');
    }
  }, [
    canCreateAnnotation,
    editingAnnotation,
    editContent,
    editPart,
    editEmotion,
    rawVideoUrl,
    teacherClassroomId,
    currentUserId,
  ]);

  const handleDeleteAnnotation = useCallback(
    async annotationId => {
      if (!rawVideoUrl) return;

      try {
        // Extract video ID from S3 URL
        const videoId = extractVideoIdFromS3Url(rawVideoUrl);

        // Delete annotation via API
        const response = await deleteAnnotationAPI(videoId, annotationId);

        if (response.success) {
          trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_DELETED, {
            teacher_classroom_id: teacherClassroomId,
            annotation_id: annotationId,
            user_id: currentUserId,
          });
          // Update local state
          setAnnotations(prev => prev.filter(ann => ann.annotationId !== annotationId));
        } else {
          throw new Error('Failed to delete annotation');
        }
      } catch (error) {
        console.error('Error deleting annotation:', error);
        trackEvent(EVENT_TYPE.TRIAL_ANNOTATION_DELETE_FAILED, {
          teacher_classroom_id: teacherClassroomId,
          annotation_id: annotationId,
          error: error.message,
          user_id: currentUserId,
        });
        alert('Failed to delete annotation. Please try again.');
      }
    },
    [rawVideoUrl, teacherClassroomId, currentUserId],
  );

  const handleSeekToAnnotation = useCallback(
    time => {
      handleSeek(time);
    },
    [handleSeek],
  );

  const handleBack = () => {
    // Navigate back to dashboard (state will be restored from sessionStorage)
    history.push('/trial-dashboard/');
  };

  if (!trial) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <TrialDetails>
              <TrialTitle variant="h5">Trial Not Found</TrialTitle>
            </TrialDetails>
          </HeaderLeft>
        </Header>
        <ErrorContainer>
          <ErrorText variant="h5">Trial Not Found</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <TrialDetails>
              <TrialTitle variant="h5">Loading...</TrialTitle>
            </TrialDetails>
          </HeaderLeft>
        </Header>
        <ErrorContainer>
          <ErrorText variant="h5">Loading video...</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <TrialDetails>
              <TrialTitle variant="h5">Error</TrialTitle>
            </TrialDetails>
          </HeaderLeft>
        </Header>
        <ErrorContainer>
          <ErrorText variant="h5">Error: {error}</ErrorText>
          <Button onClick={handleBack}>← Back to Dashboard</Button>
        </ErrorContainer>
      </Container>
    );
  }

  if (!videoUrl) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <TrialDetails>
              <TrialTitle variant="h5">No Video</TrialTitle>
            </TrialDetails>
          </HeaderLeft>
        </Header>
        <ErrorContainer>
          <ErrorText variant="h5">No video available for this trial</ErrorText>
          <Button onClick={handleBack}>← Back to Dashboard</Button>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <TrialDetails>
            <TrialTitle variant="h5">
              {trial.tutorName} / {trial.studentName} (Grade {trial.grade})
            </TrialTitle>
            <TrialMeta>
              <span>{trial.trialDate}</span>
              <span>•</span>
              <TrialTags>
                <RegionTag region={trial.region}>{trial.region}</RegionTag>
                <Tag>{trial.channel}</Tag>
                <Tag>{trial.trialVersion}</Tag>
                <SubjectTag subject={trial.subject}>{trial.subject}</SubjectTag>
                <EnrolledBadge enrolled={trial.enrolled}>
                  {trial.enrolled || 'Unknown'}
                </EnrolledBadge>
              </TrialTags>
            </TrialMeta>
          </TrialDetails>
        </HeaderLeft>
        <HeaderRight>
          <DurationDisplay>
            <DurationLabel variant="caption">Duration</DurationLabel>
            <DurationValue variant="body1Regular">{trial.duration}</DurationValue>
          </DurationDisplay>
        </HeaderRight>
      </Header>

      <MainContent>
        <ErrorBoundary
          title="Video Player Error"
          message="Unable to load the video player. Please try refreshing the page."
        >
          <VideoSection>
            <VideoPlayer
              videoUrl={videoUrl}
              videoRef={videoElementRef}
              currentTime={currentTime}
              duration={duration}
              playing={playing}
              playbackRate={playbackRate}
              annotations={annotations}
              aiAnalysisMarkers={aiAnalysisMarkers}
              onTimeUpdate={handleTimeUpdate}
              onPlay={handlePlay}
              onPause={handlePause}
              onSeek={handleSeek}
              onPlaybackRateChange={handlePlaybackRateChange}
              onTogglePlay={handleTogglePlay}
              onSeekBackward={handleSeekBackward}
              onSeekForward={handleSeekForward}
              onStartAnnotation={handleStartAnnotation}
              onDurationChange={handleDurationChange}
              onPlayerReady={handlePlayerReady}
              playerReady={playerReady}
            />
          </VideoSection>
        </ErrorBoundary>

        <ErrorBoundary
          title="Annotation Panel Error"
          message="Unable to load the annotation panel. Please try refreshing the page."
        >
          <SidePanel>
            <TabContainer>
              <Tab
                active={activeTab === 'transcript'}
                onClick={() => setActiveTab('transcript')}
              >
                Transcript
              </Tab>
              <Tab
                active={activeTab === 'annotations'}
                onClick={() => setActiveTab('annotations')}
              >
                Annotations ({annotations.length})
              </Tab>
              <Tab
                active={activeTab === 'salesPitch'}
                onClick={() => setActiveTab('salesPitch')}
              >
                Sales Pitch
              </Tab>
              <Tab
                active={activeTab === 'aiAnalysis'}
                onClick={() => setActiveTab('aiAnalysis')}
              >
                AI Analysis ({aiAnalysis?.issues?.length || 0})
              </Tab>
            </TabContainer>

            <TabContent>
              {activeTab === 'transcript' && (
                <>
                  {transcriptLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px',
                        color: COLORS.GRAY_DARK,
                      }}
                    >
                      <Text variant="body2Regular">Loading transcript...</Text>
                    </div>
                  )}
                  {transcriptProcessing && !transcriptLoading && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '12px',
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '3px solid #ffc107',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        <Text
                          variant="body2Regular"
                          style={{ color: '#856404', fontWeight: '500' }}
                        >
                          Generating Transcript
                        </Text>
                      </div>
                      <Text
                        variant="caption"
                        style={{ color: '#856404', textAlign: 'center' }}
                      >
                        {transcriptMessage ||
                          `This may take 2-10 minutes. Auto-polling every ${TRANSCRIPT_POLLING.INTERVAL_MS /
                            1000} seconds.`}
                      </Text>
                    </div>
                  )}
                  {transcriptMessage && !transcriptProcessing && !transcriptLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '12px',
                        gap: '8px',
                        background: '#d4edda',
                        border: '1px solid #28a745',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text variant="body2Regular" style={{ color: '#155724' }}>
                        {transcriptMessage}
                      </Text>
                    </div>
                  )}
                  {transcriptError && !transcriptLoading && !transcriptProcessing && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '8px',
                        background: '#f8d7da',
                        border: '1px solid #dc3545',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text
                        variant="body2Regular"
                        style={{ color: '#721c24', fontWeight: '500' }}
                      >
                        {transcriptError}
                      </Text>
                      <Text variant="caption" style={{ color: '#721c24' }}>
                        Using fallback transcript data
                      </Text>
                    </div>
                  )}
                  <TranscriptPanel
                    transcript={transcript}
                    currentTime={currentTime}
                    onSeekToTime={handleSeekToAnnotation}
                    isLoading={transcriptLoading}
                    isProcessing={transcriptProcessing}
                    classroomId={teacherClassroomId}
                    demoDate={trialData?.trialDate || ''}
                  />
                </>
              )}

              {activeTab === 'annotations' && (
                <>
                  {annotationsLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px',
                        color: COLORS.GRAY_DARK,
                      }}
                    >
                      <Text variant="body2Regular">Loading annotations...</Text>
                    </div>
                  )}

                  {!annotationsLoading && (
                    <>
                      {isCreatingAnnotation && (
                        <AnnotationForm
                          isCreating={true}
                          annotationStart={annotationStart}
                          annotationEnd={annotationEnd}
                          content={annotationContent}
                          trialPart={annotationPart}
                          emotion={annotationEmotion}
                          onContentChange={setAnnotationContent}
                          onTrialPartChange={setAnnotationPart}
                          onEmotionChange={setAnnotationEmotion}
                          onSave={handleCreateAnnotation}
                          onCancel={handleResetAnnotationForm}
                        />
                      )}

                      {editingAnnotation && (
                        <AnnotationForm
                          isEditing={true}
                          annotationStart={editingAnnotation.timestamp.start}
                          annotationEnd={editingAnnotation.timestamp.end}
                          content={editContent}
                          trialPart={editPart}
                          emotion={editEmotion}
                          onContentChange={setEditContent}
                          onTrialPartChange={setEditPart}
                          onEmotionChange={setEditEmotion}
                          onSave={handleSaveEditedAnnotation}
                          onCancel={() => setEditingAnnotation(null)}
                        />
                      )}

                      <AnnotationList
                        annotations={annotations.filter(
                          ann =>
                            !editingAnnotation ||
                            ann.annotationId !== editingAnnotation.annotationId,
                        )}
                        newlyCreatedAnnotation={newlyCreatedAnnotation}
                        onSeekToAnnotation={handleSeekToAnnotation}
                        onEditAnnotation={handleEditAnnotation}
                        onDeleteAnnotation={handleDeleteAnnotation}
                        onStartAnnotation={handleStartAnnotation}
                        isEditing={!!editingAnnotation}
                        currentUserId={currentUserId}
                        classroomId={teacherClassroomId}
                        demoDate={trialData?.trialDate || ''}
                      />
                    </>
                  )}
                </>
              )}

              {activeTab === 'salesPitch' && (
                <>
                  {salesPitchLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px',
                        color: COLORS.GRAY_DARK,
                      }}
                    >
                      <Text variant="body2Regular">Loading sales pitch...</Text>
                    </div>
                  )}
                  {salesPitchProcessing && !salesPitchLoading && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '12px',
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '3px solid #ffc107',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        <Text
                          variant="body2Regular"
                          style={{ color: '#856404', fontWeight: '500' }}
                        >
                          Generating Sales Pitch
                        </Text>
                      </div>
                      <Text
                        variant="caption"
                        style={{ color: '#856404', textAlign: 'center' }}
                      >
                        {salesPitchMessage ||
                          SUCCESS_MESSAGES.SALES_PITCH.DURATION_MESSAGE}
                      </Text>
                    </div>
                  )}
                  {salesPitchMessage && !salesPitchProcessing && !salesPitchLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '12px',
                        gap: '8px',
                        background: '#d4edda',
                        border: '1px solid #28a745',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text variant="body2Regular" style={{ color: '#155724' }}>
                        {salesPitchMessage}
                      </Text>
                    </div>
                  )}
                  {salesPitchError && !salesPitchLoading && !salesPitchProcessing && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '8px',
                        background: '#f8d7da',
                        border: '1px solid #dc3545',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text
                        variant="body2Regular"
                        style={{ color: '#721c24', fontWeight: '500' }}
                      >
                        {salesPitchError}
                      </Text>
                    </div>
                  )}
                  {!salesPitchLoading && !salesPitch && !salesPitchProcessing && (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '40px 24px',
                        color: '#5a5a5a',
                        background: '#f0f0f0',
                        border: '1px solid #c4c4c4',
                        borderRadius: '8px',
                      }}
                    >
                      <Text variant="body1Regular" style={{ marginBottom: '8px' }}>
                        No sales pitch available
                      </Text>
                      <Text variant="body2Regular" style={{ marginBottom: '16px' }}>
                        {canGenerateSalesPitch
                          ? 'Click the button below to generate sales pitch for this trial.'
                          : 'You do not have permission to generate sales pitches.'}
                      </Text>
                      <Button
                        onClick={handleGenerateSalesPitch}
                        disabled={!canGenerateSalesPitch}
                        title={
                          !canGenerateSalesPitch
                            ? 'You do not have permission to generate sales pitches'
                            : undefined
                        }
                        style={{
                          background: canGenerateSalesPitch ? COLORS.COLA : '#cccccc',
                          color: COLORS.WHITE,
                          padding: '8px 16px',
                          fontSize: '12px',
                          cursor: canGenerateSalesPitch ? 'pointer' : 'not-allowed',
                          opacity: canGenerateSalesPitch ? 1 : 0.6,
                        }}
                      >
                        ➕ Generate Sales Pitch
                      </Button>
                    </div>
                  )}

                  {/* Pass salesPitch data to panel - it will use mock data if API data is not available */}
                  <SalesPitchPanel
                    salesPitchData={
                      salesPitch?.value?.data?.sales_pitch ||
                      salesPitch?.value?.data ||
                      null
                    }
                    videoTimeSec={currentTime}
                    onSeek={handleSeek}
                    classroomId={teacherClassroomId}
                    demoDate={trialData?.trialDate || ''}
                  />
                </>
              )}

              {activeTab === 'aiAnalysis' && (
                <>
                  {aiAnalysisLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '40px',
                        color: COLORS.GRAY_DARK,
                      }}
                    >
                      <Text variant="body2Regular">Loading AI analysis...</Text>
                    </div>
                  )}
                  {aiAnalysisProcessing && !aiAnalysisLoading && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '12px',
                        background: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '3px solid #ffc107',
                            borderTop: '3px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                          }}
                        />
                        <Text
                          variant="body2Regular"
                          style={{ color: '#856404', fontWeight: '500' }}
                        >
                          Generating AI Analysis
                        </Text>
                      </div>
                      <Text
                        variant="caption"
                        style={{ color: '#856404', textAlign: 'center' }}
                      >
                        This may take a few minutes...
                      </Text>
                    </div>
                  )}
                  {aiAnalysisMessage && !aiAnalysisProcessing && !aiAnalysisLoading && (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '12px',
                        gap: '8px',
                        background: '#d4edda',
                        border: '1px solid #28a745',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text variant="body2Regular" style={{ color: '#155724' }}>
                        {aiAnalysisMessage}
                      </Text>
                    </div>
                  )}
                  {aiAnalysisError && !aiAnalysisLoading && !aiAnalysisProcessing && (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px',
                        gap: '8px',
                        background: '#f8d7da',
                        border: '1px solid #dc3545',
                        borderRadius: '8px',
                        margin: '0 0 16px 0',
                      }}
                    >
                      <Text
                        variant="body2Regular"
                        style={{ color: '#721c24', fontWeight: '500' }}
                      >
                        {aiAnalysisError}
                      </Text>
                    </div>
                  )}
                  <AIAnalysisPanel
                    aiAnalysisData={aiAnalysis}
                    videoTimeSec={currentTime}
                    onSeek={handleSeek}
                    onGenerateAIAnalysis={handleGenerateAIAnalysis}
                    isLoading={aiAnalysisLoading}
                    isProcessing={aiAnalysisProcessing}
                    canGenerateAIAnalysis={canGenerateAIAnalysis}
                    classroomId={teacherClassroomId}
                    demoDate={trialData?.trialDate || ''}
                  />
                </>
              )}
            </TabContent>
          </SidePanel>
        </ErrorBoundary>
      </MainContent>
    </Container>
  );
}

export default AnnotatePage;
